import { useEffect, useRef } from 'react';

/**
 * Custom hook for managing car update throttling with localStorage persistence
 * Ensures updates don't trigger more frequently than the specified throttle time
 * 
 * @param {number} carUpdateCounter - Counter that increments when car updates occur
 * @param {function} onUpdate - Callback function to execute when throttle allows an update
 * @param {number} throttleMs - Throttle time in milliseconds (default: 60000ms = 60 seconds)
 */
export const useCarUpdateThrottle = (carUpdateCounter, onUpdate, throttleMs = 60000) => {
    const lastUpdateTimeRef = useRef(0);

    // Initialize from localStorage on mount
    useEffect(() => {
        const savedTime = localStorage.getItem('lastCarUpdateTime');
        if (savedTime) {
            lastUpdateTimeRef.current = parseInt(savedTime, 10);
        }
    }, []);

    // Listen for updates with throttling
    useEffect(() => {
        if (!carUpdateCounter) return;
        
        const now = Date.now();
        const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
        
        // Only update if throttle time has passed since last update
        if (timeSinceLastUpdate >= throttleMs) {
            lastUpdateTimeRef.current = now;
            localStorage.setItem('lastCarUpdateTime', now.toString());
            onUpdate();
        }
    }, [carUpdateCounter, onUpdate, throttleMs]);
};
